/*
  An element's content told as a short list of facts in plain words (2.4.4). Ids are left as ids here; the screen
  turns them into names with links.
*/

import type { ElementRow, Id } from "../../../api/types";
import { LINK_TYPE_WORDS, VALUE_KIND_WORDS } from "./words";
import { textOf } from "./text";

export interface Fact {
  label: string;
  /** The words of the fact. */
  text?: string;
  /** Elements the fact names, shown as links. */
  ids?: Id[];
}

function rec(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function idList(value: unknown): Id[] {
  return Array.isArray(value) ? value.filter((x): x is string => typeof x === "string") : [];
}

function one(value: unknown): Id[] {
  return typeof value === "string" && value ? [value] : [];
}

function howMany(value: unknown): string {
  return value == null || value === "" ? "any number" : String(value);
}

function yesNo(value: unknown): string {
  return value === true ? "Yes" : "No";
}

function endFacts(label: string, value: unknown): Fact[] {
  const end = rec(value);
  const id = textOf(end.range_id);
  if (!id) return [];
  const kind = textOf(end.range_kind);
  const words: Record<string, string> = { item: "an item", capability: "anything with a capability", connection: "another Connection", component_kind: "a component kind" };
  const facts: Fact[] = [{ label, ids: [id], text: `${words[kind] ?? kind}, up to ${howMany(end.max_count)}` }];
  const run = textOf(end.run_id);
  if (run) facts.push({ label: `${label}: where on the line`, ids: [run], text: "a run of this kind" });
  return facts;
}

export function contentFacts(row: Pick<ElementRow, "kind" | "content">): Fact[] {
  const c = rec(row.content);
  const facts: Array<Fact | null> = [];
  switch (row.kind) {
    case "item":
      facts.push(c.main_parent_id ? { label: "Main parent", ids: one(c.main_parent_id) } : { label: "Main parent", text: "None: a top-level item" });
      facts.push({ label: "Headings", ids: idList(c.subset_ids) });
      if (c.abstract === true) facts.push({ label: "Kind of parent", text: "A parent item that is never drawn" });
      if (c.is_connection === true) {
        facts.push({ label: "Kind of item", text: "A Connection" });
        facts.push(...endFacts("Starts at", c.from_end), ...endFacts("Finishes at", c.to_end));
      }
      facts.push({ label: "Capabilities", ids: idList(c.capability_ids) });
      break;
    case "family":
      facts.push({ label: "Library", ids: one(c.parent_id) });
      break;
    case "sub_family":
      facts.push({ label: "Family", ids: one(c.parent_id) });
      break;
    case "library":
      if (c.dexpi_half) facts.push({ label: "Half", text: textOf(c.dexpi_half) });
      break;
    case "component_kind":
      facts.push({ label: "Libraries", ids: idList(c.subset_ids) });
      if (c.main_parent_id) facts.push({ label: "Kind of", ids: one(c.main_parent_id) });
      facts.push({ label: "Tie-in note", text: textOf(c.tie_in_note) || "None: nothing ends here" });
      facts.push({ label: "Capabilities", ids: idList(c.capability_ids) });
      break;
    case "component":
      facts.push({ label: "Item", ids: one(c.item_id) }, { label: "Component kind", ids: one(c.component_kind_id) });
      facts.push({ label: "How many", text: howMany(c.max_count) });
      if (c.ordered === true) facts.push({ label: "In order", text: "Yes" });
      if (c.sits_in_component_id) facts.push({ label: "Sits in", ids: one(c.sits_in_component_id) });
      break;
    case "capability":
      facts.push({ label: "Links and Connections may end at it", text: yesNo(c.is_link_end) });
      if (c.starter_set === true) facts.push({ label: "Comes with the starter set", text: "Yes" });
      break;
    case "data_field": {
      const unit = rec(c.unit);
      facts.push({ label: "Kind of value", text: (VALUE_KIND_WORDS as Record<string, string>)[textOf(c.value_kind)] ?? textOf(c.value_kind) });
      facts.push(c.home_kind === "shared" ? { label: "Lives in", text: "Shared by several items" } : { label: "Lives in", ids: one(c.home_id) });
      if (textOf(unit.ucum_code)) {
        const pressure = c.pressure_reference ? ` (${textOf(c.pressure_reference)})` : "";
        facts.push({ label: "Unit", text: `${textOf(unit.symbol) || textOf(unit.ucum_code)}${pressure}, measuring ${textOf(unit.quantity_kind)}` });
      }
      if (c.pick_list_id) facts.push({ label: "Pick list", ids: one(c.pick_list_id) });
      if (c.size_list_id) facts.push({ label: "Size list", ids: one(c.size_list_id) });
      if (c.project_list_kind) facts.push({ label: "Kind of project list", text: textOf(c.project_list_kind) });
      if (Array.isArray(c.table_columns)) {
        facts.push({ label: "Columns", text: c.table_columns.map((col) => textOf(rec(col).title) || textOf(rec(col).code)).join(", ") });
      }
      if (c.per_operating_case === true) facts.push({ label: "One value for each case", text: "Yes" });
      if (c.multivalued === true) facts.push({ label: "May hold several values", text: "Yes" });
      if (c.value_supplied_later === true) facts.push({ label: "Value supplied later", text: "Yes" });
      if (textOf(c.written_rule)) facts.push({ label: "Written rule", text: textOf(c.written_rule) });
      if (textOf(c.calculated_from)) facts.push({ label: "Calculated from", text: textOf(c.calculated_from) });
      if (c.data_group_id) facts.push({ label: "Data group", ids: one(c.data_group_id) });
      if (c.required_from_default) facts.push({ label: "Required from", text: textOf(c.required_from_default) });
      break;
    }
    case "data_group":
      if (c.rank != null) facts.push({ label: "Place among the groups", text: String(c.rank) });
      break;
    case "pick_list": {
      const words: Record<string, string> = { pick_list: "A pick list", size_designation: "A size designation list", exchange_table: "An exchange table" };
      facts.push({ label: "Kind of list", text: words[textOf(c.list_kind)] ?? textOf(c.list_kind) });
      facts.push({ label: "Used by", ids: idList(c.serves_field_ids) });
      const exchange = rec(c.exchange);
      if (textOf(exchange.library)) facts.push({ label: "Exchanges with", text: `${textOf(exchange.library)}, property ${textOf(exchange.library_property)}` });
      break;
    }
    case "document_kind": {
      const slots = Array.isArray(c.link_slots) ? c.link_slots.map((slot) => `${textOf(rec(slot).code)} (${(LINK_TYPE_WORDS as Record<string, string>)[textOf(rec(slot).link_type)] ?? textOf(rec(slot).link_type)})`) : [];
      facts.push({ label: "Kind of deliverable", text: textOf(c.deliverable_kind) || "Not said" });
      facts.push({ label: "A drawing", text: yesNo(c.is_drawing) }, { label: "On the target list", text: yesNo(c.on_target_list) });
      if (c.main_parent_id) facts.push({ label: "Kind of", ids: one(c.main_parent_id) });
      if (slots.length > 0) facts.push({ label: "Narrower links", text: slots.join(", ") });
      if (Array.isArray(c.operating_cases) && c.operating_cases.length > 0) facts.push({ label: "Operating cases", text: c.operating_cases.map(textOf).join(", ") });
      break;
    }
    case "document_link":
      facts.push({ label: "Starts at", ids: one(c.owner_id) });
      facts.push({ label: "What it says", text: (LINK_TYPE_WORDS as Record<string, string>)[textOf(c.link_type)] ?? textOf(c.link_type) });
      facts.push({ label: "Kind of document", ids: one(c.document_kind_id) });
      facts.push({ label: "Link name", text: textOf(c.link_slot_code) });
      break;
    case "link":
      facts.push(c.shared === true ? { label: "Starts at", text: "Shared by several items" } : { label: "Starts at", ids: one(c.owner_id) });
      facts.push({ label: "Name at the far end", text: textOf(c.reverse_name) });
      facts.push({ label: "Points to", ids: one(c.range_id) });
      facts.push({ label: "How many", text: howMany(c.max_count) });
      if (c.ordered === true) facts.push({ label: "In order", text: "Yes" });
      break;
    case "spec_chapter":
      facts.push({ label: "Title", text: textOf(c.title) });
      if (Array.isArray(c.sections)) facts.push({ label: "Sections", text: String(c.sections.length) });
      break;
    case "framework":
      facts.push({ label: "Section", text: textOf(c.section) }, { label: "File", text: textOf(c.file) });
      break;
  }
  return facts.filter((fact): fact is Fact => fact !== null && ((fact.text ?? "") !== "" || (fact.ids?.length ?? 0) > 0));
}
